import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { AlbumCategory } from '../components/Hub/Albums/useAlbumIcon';

export type MembershipStatus = 'active' | 'expired';

export type PromptStatus = 'todo' | 'submitting' | 'completed' | 'error';

// Public-safe shape returned by the shared (unauthenticated) album endpoint.
// Anything in here is exposed to anyone with the share URL — keep it minimal.
export interface SharedPrompt {
  id: string;
  title: string;
  images?: string[];
  caption?: string;
}

export interface SharedAlbum {
  id: string;
  title: string;
  description: string;
  petName: string;
  petType: 'dog' | 'cat';
  photoCount: number;
  prompts: SharedPrompt[];
}

// Owner-facing shapes — supersets of the shared shapes. The `extends` makes the
// dependency explicit so it's obvious that anything added here stays private
// unless it's also added to the Shared* counterpart.
export interface Prompt extends SharedPrompt {
  description: string;
  status: PromptStatus;
  createdAt?: string;
}

export interface Album extends Omit<SharedAlbum, 'prompts'> {
  category: AlbumCategory;
  completed: number;
  total: number;
  isLocked: boolean;
  prompts: Prompt[];
}

export interface DevSettingsOverrides {
  trialStatus: MembershipStatus;
  premiumStatus: MembershipStatus;
  mockAlbums: Album[];
  showManyPets: boolean;
  minPromptsComplete: boolean;
  allPromptsComplete: boolean;
  simulateUploadFailure: boolean;
}

export interface DevSettingsActions {
  setTrialStatus: (status: MembershipStatus) => void;
  setPremiumStatus: (status: MembershipStatus) => void;
  setMockAlbums: (albums: Album[]) => void;
  setShowManyPets: (showMany: boolean) => void;
  setMinPromptsComplete: (complete: boolean) => void;
  setAllPromptsComplete: (complete: boolean) => void;
  setSimulateUploadFailure: (simulate: boolean) => void;
  resetToDefaults: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  enhancePetParentData: (realData: any) => any;
  getAlbumMockQuery: ({ variables }: { variables: { id: string } }) => {
    data: Album | null;
  };
  getSharedAlbumMockQuery: ({ variables }: { variables: { id: string } }) => {
    data: SharedAlbum | null;
  };
  markMockPromptSubmitting: (albumId: string, promptId: string) => void;
  getPromptMockQuery: ({ variables }: { variables: { id: string } }) => {
    data: Prompt | null;
  };
  deleteMockPromptMutation: ({ variables }: { variables: { id: string } }) => {
    data: Prompt | null;
  };
}

export type DevSettingsContextType = DevSettingsOverrides &
  DevSettingsActions & {
    isDevMode: boolean;
  };

// Mock photo + caption used for successful Typeform submissions in dev mode.
// In production these come from the backend after the Typeform webhook lands.
const MOCK_SUBMITTED_IMAGE = 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800';
const MOCK_DOG_IMAGE_2 = 'https://images.unsplash.com/photo-1561037404-61cd46aa615b?w=800';
const MOCK_DOG_IMAGE_3 = 'https://images.unsplash.com/photo-1558788353-f76d92427f16?w=800';
const MOCK_CAT_IMAGE_1 = 'https://images.unsplash.com/photo-1574144611937-0df059b5ef3e?w=800';
const MOCK_CAT_IMAGE_2 = 'https://images.unsplash.com/photo-1592194996308-7b43878e84a6?w=800';
const MOCK_CAT_IMAGE_3 = 'https://images.unsplash.com/photo-1573865526739-10659fec78a5?w=800';
const MOCK_SUBMITTED_CAPTION = 'When I decided to adopt a dog from a shelter, I didn’t know that this encounter would change my life. I was looking for a friend who could become part of our family, but I already had a cat named Luna, and I was worried about how they would get along. That’s how Charlie came into my life, a German Shepherd with kind eyes and a gentle nature. At the shelter, he was very shy and timid, but I immediately felt that we were meant for each other.';
const MOCK_SHORT_CAPTION = 'First night home — already claimed the couch as their own.';

const dogWelcomeHomePrompts: Prompt[] = [
  {
    id: 'wh-dog-1',
    title: 'Introducing [pet name]',
    description:
      'Snap a photo of [pet name] and share the story behind how they got their name.',
    status: 'completed',
    images: [MOCK_SUBMITTED_IMAGE],
    caption: MOCK_SUBMITTED_CAPTION,
    createdAt: '2025-08-07T10:00:00Z',
  },
  {
    id: 'wh-dog-2',
    title: 'Welcome Home Look',
    description: 'Show [pet name]’s face when they truly looked at home.',
    status: 'completed',
    images: [
      MOCK_SUBMITTED_IMAGE,
      MOCK_DOG_IMAGE_2,
      MOCK_DOG_IMAGE_3,
      MOCK_SUBMITTED_IMAGE,
    ],
    caption: MOCK_SHORT_CAPTION,
    createdAt: '2025-08-12T14:30:00Z',
  },
  {
    id: 'wh-dog-3',
    title: 'First Nap',
    description:
      'Capture where [pet name] chose to nap or feel safe in your home.',
    status: 'completed',
    images: [MOCK_DOG_IMAGE_2, MOCK_DOG_IMAGE_3],
    caption: 'Found their favorite spot under the window.',
    createdAt: '2025-08-15T09:15:00Z',
  },
  {
    id: 'wh-dog-4',
    title: 'Morning Routine',
    description:
      'How do you and [pet name] start the day? Snap a photo of your morning routine together.',
    status: 'submitting',
  },
  {
    id: 'wh-dog-5',
    title: 'Building Trust',
    description:
      'When did [pet name] first show they trusted you? Capture that special moment in a photo.',
    status: 'error',
  },
  {
    id: 'wh-dog-6',
    title: 'First Toy',
    description:
      'What toy did [pet name] choose first? Share a photo of them enjoying it.',
    status: 'todo',
  },
  {
    id: 'wh-dog-7',
    title: 'First Walk Together',
    description:
      'Capture a photo from your first walk or outing with [pet name]—a favorite route, park, or trail.',
    status: 'todo',
  },
  {
    id: 'wh-dog-8',
    title: 'First Outdoor Adventure',
    description:
      'Capture [pet name] exploring the yard or outdoor space for the first time.',
    status: 'todo',
  },
  {
    id: 'wh-dog-9',
    title: 'First Outing',
    description:
      'Where did you take [pet name] on your first adventure together? Share a photo from that day.',
    status: 'todo',
  },
  {
    id: 'wh-dog-10',
    title: 'First Friends',
    description:
      'Share a photo of [pet name] with someone they’ve bonded with—animal or human.',
    status: 'todo',
  },
  {
    id: 'wh-dog-11',
    title: 'First Family Photo',
    description:
      'Share a photo of [pet name] with family—whether people or other pets.',
    status: 'todo',
  },
  {
    id: 'wh-dog-12',
    title: 'Silly Habits',
    description:
      'What’s [pet name]’s quirkiest habit? Capture it in a photo that makes you smile.',
    status: 'todo',
  },
  {
    id: 'wh-dog-13',
    title: 'First Days Together',
    description:
      'What did those first few days feel like? Share a photo that captures your new life together.',
    status: 'todo',
  },
];

const catWelcomeHomePrompts: Prompt[] = [
  {
    id: 'wh-cat-1',
    title: 'Introducing [pet name]',
    description:
      'Snap a photo of [pet name] and share the story behind how they got their name.',
    status: 'completed',
    images: [MOCK_CAT_IMAGE_1, MOCK_CAT_IMAGE_2, MOCK_CAT_IMAGE_3],
    caption: MOCK_SUBMITTED_CAPTION,
    createdAt: '2025-08-07T10:00:00Z',
  },
  {
    id: 'wh-cat-2',
    title: 'Welcome Home Look',
    description: 'Show [pet name]’s face when they truly looked at home.',
    status: 'completed',
    images: [MOCK_CAT_IMAGE_2],
    caption: MOCK_SHORT_CAPTION,
    createdAt: '2025-08-10T18:00:00Z',
  },
  {
    id: 'wh-cat-3',
    title: 'First Nap',
    description: 'Capture where [pet name] chose to nap in your home.',
    status: 'error',
  },
  {
    id: 'wh-cat-4',
    title: 'Morning Routine',
    description:
      'How do you and [pet name] start the day? Snap a photo of your morning routine together.',
    status: 'submitting',
  },
  {
    id: 'wh-cat-5',
    title: 'Building Trust',
    description:
      'When did [pet name] first show they trusted you? Capture that special moment in a photo.',
    status: 'todo',
  },
  {
    id: 'wh-cat-6',
    title: 'First Toy',
    description:
      'What toy did [pet name] choose first? Share a photo of them enjoying it.',
    status: 'todo',
  },
  {
    id: 'wh-cat-7',
    title: 'First Window Watch',
    description:
      'Capture [pet name] perched by a window or watching the world outside for the first time.',
    status: 'todo',
  },
  {
    id: 'wh-cat-8',
    title: 'First Climb',
    description:
      'Show [pet name] exploring high places—like a cat tree, shelf, or windowsill.',
    status: 'todo',
  },
  {
    id: 'wh-cat-9',
    title: 'First Outdoor View',
    description:
      'Share a photo of [pet name] safely exploring the outdoors from a window, catio, or harness.',
    status: 'todo',
  },
  {
    id: 'wh-cat-10',
    title: 'First Friends',
    description:
      'Share a photo of [pet name] with someone they’ve bonded with—animal or human.',
    status: 'todo',
  },
  {
    id: 'wh-cat-11',
    title: 'First Family Photo',
    description:
      'Share a photo of [pet name] with family—whether people or other pets.',
    status: 'todo',
  },
  {
    id: 'wh-cat-12',
    title: 'Silly Habits',
    description:
      'What’s [pet name]’s quirkiest habit? Capture it in a photo that makes you smile.',
    status: 'todo',
  },
  {
    id: 'wh-cat-13',
    title: 'First Days Together',
    description:
      'What did those first few days feel like? Share a photo that captures your new life together.',
    status: 'todo',
  },
];

const countCompleted = (prompts: Prompt[]) => prompts.filter((p) => p.status === 'completed').length;
const countPhotos = (prompts: Prompt[]) => prompts.reduce(
    (sum, p) => sum + (p.status === 'completed' ? p.images?.length || 0 : 0),
    0,
  );

// For the `minPromptsComplete` dev override: force the first N prompts to
// 'completed' so we can preview the unlock state, while keeping everything
// after the threshold at its real status.
const statusForMinComplete = (
  index: number,
  threshold: number,
  current: PromptStatus,
): PromptStatus => (index < threshold ? 'completed' : current);

const WELCOME_HOME_DESCRIPTION = 'Celebrate your pet’s arrival and those first sweet moments together.';

const dogWelcomeHomeAlbum: Album = {
  id: 'welcome-home-dog',
  title: 'Welcome Home',
  description: WELCOME_HOME_DESCRIPTION,
  category: 'welcome-home',
  petName: 'Charlie',
  petType: 'dog',
  completed: countCompleted(dogWelcomeHomePrompts),
  total: dogWelcomeHomePrompts.length,
  photoCount: countPhotos(dogWelcomeHomePrompts),
  isLocked: false,
  prompts: dogWelcomeHomePrompts,
};

const catWelcomeHomeAlbum: Album = {
  id: 'welcome-home-cat',
  title: 'Welcome Home',
  description: WELCOME_HOME_DESCRIPTION,
  category: 'welcome-home',
  petName: 'Whiskers',
  petType: 'cat',
  completed: countCompleted(catWelcomeHomePrompts),
  total: catWelcomeHomePrompts.length,
  photoCount: countPhotos(catWelcomeHomePrompts),
  isLocked: false,
  prompts: catWelcomeHomePrompts,
};

const defaultMockAlbums: Album[] = [dogWelcomeHomeAlbum, catWelcomeHomeAlbum];

const defaultOverrides: DevSettingsOverrides = {
  trialStatus: 'active',
  premiumStatus: 'expired',
  mockAlbums: defaultMockAlbums,
  showManyPets: true,
  minPromptsComplete: false,
  allPromptsComplete: false,
  simulateUploadFailure: false,
};

const DevSettingsContext = createContext<DevSettingsContextType | null>(null);

export interface DevSettingsProviderProps {
  children: ReactNode;
}

export function DevSettingsProvider({ children }: DevSettingsProviderProps) {
  const [overrides, setOverrides] = useState<DevSettingsOverrides>(defaultOverrides);
  const isDevMode = process.env.REACT_APP_CONTEXT !== 'production';
  // Read latest simulateUploadFailure inside async setTimeout without re-creating submitPrompt.
  const simulateUploadFailureRef = useRef(overrides.simulateUploadFailure);
  simulateUploadFailureRef.current = overrides.simulateUploadFailure;

  const setTrialStatus = useCallback((status: MembershipStatus) => {
    setOverrides((prev) => ({ ...prev, trialStatus: status }));
  }, []);

  const setPremiumStatus = useCallback((status: MembershipStatus) => {
    setOverrides((prev) => ({ ...prev, premiumStatus: status }));
  }, []);

  const setMockAlbums = useCallback((albums: Album[]) => {
    setOverrides((prev) => ({ ...prev, mockAlbums: albums }));
  }, []);

  const setShowManyPets = useCallback((showMany: boolean) => {
    setOverrides((prev) => ({ ...prev, showManyPets: showMany }));
  }, []);

  const setMinPromptsComplete = useCallback((complete: boolean) => {
    setOverrides((prev) => ({ ...prev, minPromptsComplete: complete }));
  }, []);

  const setAllPromptsComplete = useCallback((complete: boolean) => {
    setOverrides((prev) => ({ ...prev, allPromptsComplete: complete }));
  }, []);

  const setSimulateUploadFailure = useCallback((simulate: boolean) => {
    setOverrides((prev) => ({ ...prev, simulateUploadFailure: simulate }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setOverrides(defaultOverrides);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const enhancePetParentData = useCallback(
    (petParent: any) => {
      if (!petParent || !isDevMode) {
        return petParent;
      }

      const enhanced = {
        ...petParent,
        trialStatus: overrides.trialStatus,
        premiumStatus: overrides.premiumStatus,
      };

      if (enhanced.animals) {
        if (overrides.showManyPets) {
          if (enhanced.animals.length === 1) {
            const originalPet = enhanced.animals[0];
            const mockPet = {
              ...originalPet,
              id: 'mock-pet-999',
              name:
                originalPet.animalType?.toLowerCase() === 'dog'
                  ? 'Bella'
                  : 'Whiskers',
              animalType:
                originalPet.animalType?.toLowerCase() === 'dog' ? 'Cat' : 'Dog',
              primaryBreed:
                originalPet.animalType?.toLowerCase() === 'dog'
                  ? 'Siamese'
                  : 'Golden Retriever',
              microchipNumber: '985112345678901',
              animalMedias: [
                {
                  dateCreated: '2024-03-15T10:30:00Z',
                  isPrimary: true,
                  id: 'mock-media-1',
                  fileType: 'pdf',
                  url: 'https://example.com/shelterbuddy-records/whiskers-medical.pdf',
                  pdfMonkeyUrl: null,
                  awsUrl: {
                    url: 'https://example.com/shelterbuddy-records/whiskers-medical.pdf',
                  },
                },
              ],
            };
            enhanced.animals = [originalPet, mockPet];
          }
        } else {
          enhanced.animals = enhanced.animals.slice(0, 1);
        }
      }

      if (enhanced.awo && overrides.showManyPets) {
        enhanced.awo = {
          ...enhanced.awo,
          faqLink: enhanced.awo.faqLink || 'https://example.com/faqs',
          adoptionPacketLinkCats:
            enhanced.awo.adoptionPacketLinkCats
            || 'https://example.com/adoption-packet-cats.pdf',
          adoptionPacketLinkDogs:
            enhanced.awo.adoptionPacketLinkDogs
            || 'https://example.com/adoption-packet-dogs.pdf',
        };
      }

      return enhanced;
    },
    [
      overrides.trialStatus,
      overrides.premiumStatus,
      overrides.showManyPets,
      isDevMode,
    ],
  );

  const updatePromptStatus = useCallback(
    (albumId: string, promptId: string, updater: (p: Prompt) => Prompt) => {
      setOverrides((prev) => ({
        ...prev,
        mockAlbums: prev.mockAlbums.map((album) => {
          if (album.id !== albumId) return album;
          const newPrompts = album.prompts.map((p) => (p.id === promptId ? updater(p) : p));
          return {
            ...album,
            prompts: newPrompts,
            completed: countCompleted(newPrompts),
            total: newPrompts.length,
            photoCount: countPhotos(newPrompts),
          };
        }),
      }));
    },
    [],
  );

  // Mock helper used by the variables-shaped query/mutation mocks below.
  // Looks the prompt up by its globally-unique id (real Apollo queries don't
  // know about albumId — they only get the prompt id).
  const updatePromptByPromptId = useCallback(
    (promptId: string, updater: (p: Prompt) => Prompt): Prompt | null => {
      let resolved: Prompt | null = null;
      setOverrides((prev) => ({
        ...prev,
        mockAlbums: prev.mockAlbums.map((album) => {
          if (!album.prompts.some((p) => p.id === promptId)) return album;
          const newPrompts = album.prompts.map((p) => {
            if (p.id !== promptId) return p;
            const next = updater(p);
            resolved = next;
            return next;
          });
          return {
            ...album,
            prompts: newPrompts,
            completed: countCompleted(newPrompts),
            total: newPrompts.length,
            photoCount: countPhotos(newPrompts),
          };
        }),
      }));
      return resolved;
    },
    [],
  );

  const markMockPromptSubmitting = useCallback(
    (albumId: string, promptId: string) => {
      updatePromptStatus(albumId, promptId, (p) => ({
        ...p,
        status: 'submitting',
      }));
    },
    [updatePromptStatus],
  );

  const getPromptMockQuery = useCallback(
    ({ variables }: { variables: { id: string } }) => {
      const willFail = simulateUploadFailureRef.current;
      const data = updatePromptByPromptId(variables.id, (p) => {
        if (willFail) {
          return { ...p, status: 'error' };
        }
        return {
          ...p,
          status: 'completed',
          images: [MOCK_SUBMITTED_IMAGE],
          caption: MOCK_SUBMITTED_CAPTION,
          createdAt: new Date().toISOString(),
        };
      });
      return { data };
    },
    [updatePromptByPromptId],
  );

  // TODO(Phil): replace with an Apollo mutation once the BE delete endpoint
  // exists. For now we mutate the mock prompt state directly.
  const deleteMockPromptMutation = useCallback(
    ({ variables }: { variables: { id: string } }) => {
      const data = updatePromptByPromptId(variables.id, (p) => ({
        ...p,
        status: 'todo',
        images: undefined,
        caption: undefined,
        createdAt: undefined,
      }));
      return { data };
    },
    [updatePromptByPromptId],
  );

  const getAlbumMockQuery = useCallback(
    ({ variables }: { variables: { id: string } }) => {
      const album = overrides.mockAlbums.find((a) => a.id === variables.id);
      if (!album) return { data: null };

      let albumData: Album = album;

      // Dev override: force every prompt complete (overrides any per-prompt state).
      if (overrides.allPromptsComplete) {
        const newPrompts = album.prompts.map((p) => ({
          ...p,
          status: 'completed' as const,
          images: p.images || [MOCK_SUBMITTED_IMAGE],
          caption: p.caption || 'Sample caption for completed prompt.',
          createdAt: p.createdAt || '2025-08-07T10:00:00Z',
        }));
        albumData = {
          ...album,
          prompts: newPrompts,
          completed: newPrompts.length,
          photoCount: countPhotos(newPrompts),
        };
      } else if (overrides.minPromptsComplete) {
        // Dev override: force the first 3 prompts complete to test unlock state.
        const minPromptsForUnlock = 3;
        const newPrompts = album.prompts.map((p, index) => ({
          ...p,
          status: statusForMinComplete(index, minPromptsForUnlock, p.status),
          images:
            index < minPromptsForUnlock
              ? p.images || [MOCK_SUBMITTED_IMAGE]
              : p.images,
          caption:
            index < minPromptsForUnlock
              ? p.caption || 'Sample caption for completed prompt.'
              : p.caption,
          createdAt:
            index < minPromptsForUnlock
              ? p.createdAt || '2025-08-07T10:00:00Z'
              : p.createdAt,
        }));
        albumData = {
          ...album,
          prompts: newPrompts,
          completed: countCompleted(newPrompts),
          photoCount: countPhotos(newPrompts),
        };
      }

      return { data: albumData };
    },
    [
      overrides.mockAlbums,
      overrides.allPromptsComplete,
      overrides.minPromptsComplete,
    ],
  );

  // Mocks the public/share endpoint: only completed prompts with photos, and
  // only the fields that are safe to expose to anyone with the share URL.
  const getSharedAlbumMockQuery = useCallback(
    ({ variables }: { variables: { id: string } }): { data: SharedAlbum | null } => {
      const { data: album } = getAlbumMockQuery({ variables });
      if (!album) return { data: null };
      const prompts: SharedPrompt[] = album.prompts
        .filter((p) => p.status === 'completed' && p.images && p.images.length > 0)
        .map((p) => ({
          id: p.id,
          title: p.title,
          images: p.images,
          caption: p.caption,
        }));
      return {
        data: {
          id: album.id,
          title: album.title,
          description: album.description,
          petName: album.petName,
          petType: album.petType,
          photoCount: album.photoCount,
          prompts,
        },
      };
    },
    [getAlbumMockQuery],
  );

  const contextValue: DevSettingsContextType = useMemo(
    () => ({
      ...overrides,
      isDevMode,
      setTrialStatus,
      setPremiumStatus,
      setMockAlbums,
      setShowManyPets,
      setMinPromptsComplete,
      setAllPromptsComplete,
      setSimulateUploadFailure,
      resetToDefaults,
      enhancePetParentData,
      getAlbumMockQuery,
      getSharedAlbumMockQuery,
      markMockPromptSubmitting,
      getPromptMockQuery,
      deleteMockPromptMutation,
    }),
    [
      overrides,
      isDevMode,
      setTrialStatus,
      setPremiumStatus,
      setMockAlbums,
      setShowManyPets,
      setMinPromptsComplete,
      setAllPromptsComplete,
      setSimulateUploadFailure,
      resetToDefaults,
      enhancePetParentData,
      getAlbumMockQuery,
      getSharedAlbumMockQuery,
      markMockPromptSubmitting,
      getPromptMockQuery,
      deleteMockPromptMutation,
    ],
  );

  return (
    <DevSettingsContext.Provider value={contextValue}>
      {children}
    </DevSettingsContext.Provider>
  );
}

export function useDevSettings(): DevSettingsContextType {
  const context = useContext(DevSettingsContext);
  if (!context) {
    throw new Error('useDevSettings must be used within a DevSettingsProvider');
  }
  return context;
}
